import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import find from 'lodash/find';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import API, { SECONDARY_API_CLIENT } from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { CandidateEntity, ContactEntity, ContactRole } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SeverityBySimilarity } from 'UI/constants/status';
import { isValidEmail } from 'UI/utils/forms';

const CANDIDATE_INCLUDES = 'status';

const searchEmailInTalentPool = async ({ emailToSearch, signal }) => {
  try {
    const response = await SECONDARY_API_CLIENT.post(
      Endpoints.SearchCandidatesInTalentPool,
      {
        email: emailToSearch,
        offset: 0,
        order: [{ field: 'lastApplicationDate', sort: 'desc' }],
        page: 0,
        pageSize: 20,
        quickSearch: '',
        size: 20
      },
      { signal }
    );

    const {
      data: { data }
    } = response;

    return data?.length ? data[0] : null;
  } catch (error) {
    return null;
  }
};

const searchEmail = async ({ endpoint, apiVersion, entity, emailToSearch, signal }) => {
  try {
    const response = await API.get(endpoint, {
      apiVersion,
      params: {
        entityType: entity,
        keyword: emailToSearch
      },
      signal
    });

    const { data } = response;

    return (
      find(data, { email: emailToSearch }) || find(data, { personal_email: emailToSearch }) || null
    );
  } catch (error) {
    return null;
  }
};

const getEntityInfo = async ({ endpoint, apiVersion, signal }) => {
  try {
    const info = await API.get(endpoint, { apiVersion, signal });
    return info?.data;
  } catch {
    return null;
  }
};

const initialData = {
  id: null,
  company: null,
  companyId: null,
  email: null,
  entity: null,
  fullName: null,
  otherEmail: null,
  recruiter: null,
  status: null,
  subtitle: null
};

const INITIAL_STATE = {
  candidateRegisteredInTalentPool: null,
  isRegistered: false,
  isValidating: true,
  registeredData: initialData,
  severity: SeverityBySimilarity.None
};

export const useCheckEmail = ({
  debouncedEmail,
  hasEmail,
  isFromTalentPool,
  shouldCheckContacts
}) => {
  const fixedEmail = useMemo(() => (debouncedEmail || '').trim().toLowerCase(), [debouncedEmail]);

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const canCheckEmailInTalentPool = checkIfFeatureFlagEnabled(
    FeatureFlags.NewCandidateTalentPoolValidation
  );

  const { nextSignal } = useLatestRequestSignal();
  const requestIdRef = useRef(0);

  const [validation, setValidation] = useState(INITIAL_STATE);

  const safeSetValidation = useCallback((requestId, next) => {
    if (requestIdRef.current !== requestId) return;
    setValidation(next);
  }, []);

  const searchDuplicates = useCallback(
    async (emailToSearch, requestId, signal) => {
      // 1) Candidate search
      const candidateRegistered = await searchEmail({
        emailToSearch,
        endpoint: Endpoints.Search,
        apiVersion: 1,
        entity: CandidateEntity.searchParam,
        signal
      });

      if (candidateRegistered?.id) {
        const candidate = await getEntityInfo({
          endpoint: `${Endpoints.Candidates}/${candidateRegistered.id}?includes=${CANDIDATE_INCLUDES}`,
          apiVersion: 2,
          signal
        });

        return safeSetValidation(requestId, {
          ...INITIAL_STATE,
          isRegisteredAsCandidate: true,
          isValidating: false,
          isRegistered: true,
          severity: SeverityBySimilarity.Exact,
          registeredData: {
            ...initialData,
            id: candidate?.id,
            company: candidate?.current_company,
            companyId: candidate?.company_id,
            email: candidateRegistered.email,
            entity: ContactRole.Candidate,
            fullName: candidateRegistered.title,
            otherEmail: candidateRegistered.personal_email,
            recruiter: candidateRegistered.recruiter,
            status: candidate?.status,
            subtitle:
              candidateRegistered?.subtitle ||
              `${candidateRegistered.title} / ${candidateRegistered.location}`,
            result: candidate,
            searchResult: candidateRegistered
          }
        });
      }

      // 2) Talent pool search (if enabled)
      if (canCheckEmailInTalentPool && !isFromTalentPool) {
        const candidateRegisteredInTalentPool = await searchEmailInTalentPool({
          emailToSearch,
          signal
        });

        if (candidateRegisteredInTalentPool?.id) {
          return safeSetValidation(requestId, {
            ...INITIAL_STATE,

            candidateRegisteredInTalentPool,
            isRegistered: true,
            isValidating: false,
            registeredData: initialData,
            result: candidateRegisteredInTalentPool,
            searchResult: null,
            severity: SeverityBySimilarity.Exact
          });
        }
      }

      // 3) Contact search (optional)
      if (!shouldCheckContacts) {
        return safeSetValidation(requestId, { ...INITIAL_STATE, isValidating: false });
      }

      const contactRegistered = await searchEmail({
        emailToSearch,
        endpoint: Endpoints.Search,
        apiVersion: 1,
        entity: ContactEntity.searchParam,
        signal
      });

      if (contactRegistered?.id) {
        const isHiringAuthority = contactRegistered.role_id === ContactRole.HiringAuthority;

        const endpoint = `${isHiringAuthority ? Endpoints.HiringAuthorities : Endpoints.Names}/${
          contactRegistered.id
        }`;
        const apiVersion = isHiringAuthority ? 2 : 1;

        const name = await getEntityInfo({ endpoint, apiVersion, signal });

        return safeSetValidation(requestId, {
          ...INITIAL_STATE,
          isValidating: false,
          isRegistered: true,
          severity: SeverityBySimilarity.Similar,
          registeredData: {
            ...initialData,
            id: name?.id,
            company: contactRegistered.company,
            companyId: name?.company_id,
            email: contactRegistered.email,
            entity: contactRegistered.role_id,
            fullName: contactRegistered.full_name,
            otherEmail: contactRegistered.personal_email,
            recruiter: contactRegistered.recruiter_name,
            status: name?.status,
            subtitle: contactRegistered?.subtitle || contactRegistered.title,
            result: name,
            searchResult: contactRegistered
          }
        });
      }

      // 4) Nothing found
      return safeSetValidation(requestId, { ...INITIAL_STATE, isValidating: false });
    },
    [canCheckEmailInTalentPool, isFromTalentPool, shouldCheckContacts, safeSetValidation]
  );

  useEffect(() => {
    // create a new request id for this run
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const signal = nextSignal();

    // reset per run
    setValidation({ ...INITIAL_STATE, isValidating: true });

    if (!fixedEmail || !hasEmail || !isValidEmail(fixedEmail)) {
      safeSetValidation(requestId, { ...INITIAL_STATE, isValidating: false });
      return;
    }

    searchDuplicates(fixedEmail, requestId, signal);
  }, [fixedEmail, hasEmail, searchDuplicates, safeSetValidation, nextSignal]);

  return { validation, initialData };
};
